import { BusinessObjectBase } from './base';
import { profileSchema } from '@schemas/business-objects/profile';
import type { User } from './user';
import type { Id } from '../base-types';

export interface ProfileData {
  readonly id: number;
  readonly fullName?: string;
  readonly alias?: string;
  readonly userId: Id;
  readonly user?: User;
}

export interface Profile extends Readonly<ProfileData> {}
type ProfileCreateWithChildrenData = Partial<ProfileData>;

export class Profile extends BusinessObjectBase {
  static readonly entityName = 'Profile' as const;
  static readonly plural = 'profiles';
  static schema = profileSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Profile[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Profile(data));
  }

  static async findOne(filter?: any): Promise<Profile | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Profile(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<Profile> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Profile(data);
  }

  static async create(data: Omit<ProfileData, 'id'>): Promise<Profile> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Profile(result);
  }

  static async createMany(data: Omit<ProfileData, 'id'>[]): Promise<Profile[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Profile(item));
  }

  static async createWithChildren(
    data: ProfileCreateWithChildrenData,
  ): Promise<Profile> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Profile(result);
  }

  static async createManyWithChildren(
    data: ProfileCreateWithChildrenData[],
  ): Promise<Profile[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Profile(item));
  }

  static async updateById(
    id: number,
    updates: Partial<ProfileData>,
  ): Promise<Profile> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Profile(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async currentUserProfile(filter: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/current-user-profile`;
    return this.post<any>(url, filter);
  }
}
